import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { mainNav } from "@/mocks/navigation";
import { useStore } from "@/store/StoreContext";
import type { PanelKey } from "./Header";

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
  onOpenPanel: (panel: PanelKey) => void;
}

interface MenuLink {
  label: string;
  href: string;
}

const menuLinks: MenuLink[] = [{ label: "Início", href: "/" }, ...mainNav];

/**
 * Menu móvel independente.
 *
 * É renderizado em um portal ligado ao document.body, portanto NÃO fica
 * preso ao cabeçalho sticky/backdrop-blur. Surge como um painel compacto,
 * logo abaixo do cabeçalho, com rolagem interna e nunca exibe conteúdo da
 * sacola, busca ou favoritos.
 */
export default function MobileMenu({ open, onClose, onOpenPanel }: MobileMenuProps) {
  const { cartCount, favorites } = useStore();
  const [top, setTop] = useState(76);

  useEffect(() => {
    if (!open) return undefined;

    const header = document.querySelector("header");
    const bottom = header ? header.getBoundingClientRect().bottom : 0;
    setTop(Math.max(bottom + 8, 12));

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  const openPanel = (panel: PanelKey) => {
    onClose();
    onOpenPanel(panel);
  };

  const node = (
    <div
      className="fixed inset-0 z-[120] xl:hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Menu de navegação"
    >
      <button
        type="button"
        aria-label="Fechar menu"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-pointer bg-foreground-950/45 backdrop-blur-[2px]"
      />

      <div
        className="ab-panel-in absolute right-3 flex w-[72%] max-w-[278px] flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 shadow-[0_20px_50px_-24px_rgba(48,24,12,0.45)]"
        style={{ top, maxHeight: "82dvh" }}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-background-200 px-4 py-3">
          <span className="font-heading text-lg text-primary-700">Amora Belle</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar menu"
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-background-300 text-foreground-700 transition-colors hover:bg-background-100"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <nav aria-label="Menu principal" className="overflow-y-auto px-4 py-3">
          <p className="mb-1.5 text-[9px] uppercase tracking-[0.2em] text-foreground-500">Categorias</p>
          <ul className="flex flex-col">
            {menuLinks.map((item) => (
              <li key={item.label}>
                <Link
                  to={item.href}
                  onClick={onClose}
                  className="flex cursor-pointer items-center justify-between border-b border-background-200 py-2 text-[15px] text-foreground-900 transition-colors hover:text-primary-600"
                >
                  {item.label}
                  <i className="ri-arrow-right-up-line text-sm text-foreground-400" />
                </Link>
              </li>
            ))}
          </ul>

          <p className="mb-1.5 mt-4 text-[9px] uppercase tracking-[0.2em] text-foreground-500">Sua conta</p>
          <div className="grid grid-cols-3 gap-1.5">
            <button
              type="button"
              onClick={() => openPanel("search")}
              className="flex cursor-pointer flex-col items-center gap-1 rounded-lg border border-background-200 bg-background-100 py-2.5 text-[10px] text-foreground-700 transition-colors hover:bg-background-200"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-search-line text-base" />
              </span>
              Buscar
            </button>
            <button
              type="button"
              onClick={() => openPanel("favorites")}
              className="relative flex cursor-pointer flex-col items-center gap-1 rounded-lg border border-background-200 bg-background-100 py-2.5 text-[10px] text-foreground-700 transition-colors hover:bg-background-200"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-heart-3-line text-base" />
              </span>
              Favoritos
              {favorites.length > 0 ? (
                <span className="absolute right-1.5 top-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-accent-500 text-[8px] font-medium text-background-50">
                  {favorites.length}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              onClick={() => openPanel("bag")}
              className="relative flex cursor-pointer flex-col items-center gap-1 rounded-lg border border-background-200 bg-background-100 py-2.5 text-[10px] text-foreground-700 transition-colors hover:bg-background-200"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-shopping-bag-3-line text-base" />
              </span>
              Sacola
              {cartCount > 0 ? (
                <span className="absolute right-1.5 top-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary-500 text-[8px] font-medium text-background-50">
                  {cartCount}
                </span>
              ) : null}
            </button>
          </div>
        </nav>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}